import { useMemo } from 'react';
import { usePublishParts } from './publishParts';
import { PhaseControls } from './PhaseControls';
import { PublishPanel, type PublishBlock } from './PublishPanel';
import { useBrand } from './brandStore';
import { useResolvedInfo } from './infoPicks';
import { useListingSplash } from './heroCrop';
import { useAssemblySteps } from './categoryBands';
import { pieceNames, useStepsRun, subStepLabel } from './stepsRun';
import { usePartsRun } from './partsRun';
import { renderSubStepText } from './subStepText';
import { usePartModelsStage } from './stageProgress';
import { useClips, useDrawableParts } from './assemblyClip';
import { useAssemblySlots } from './assemblySlots';
import type { PublishSnapshot } from './publishApi';

// How long a sub-step's instruction may be, published — the backend's MAX_INSTRUCTION_CHARS. Longer
// than any other snapshot string: a sub-step also says what the tightenings after it ask (see
// saidWith), and a run of seven of them cut at 300 characters stopped mid-sentence.
const MAX_INSTRUCTION = 1500;

// Phase 5: publish this manual to a public, no-login page + QR.
// This tab builds the metadata snapshot (product name / category / time for the
// splash, its hero image from the listing, plus per-step labels) from the
// browser stores — that data lives only client-side — and hands it to
// PublishPanel, which owns the publish API and state. `active` is true only while
// this tab is open, so the panel refetches publish status each time it is shown.
//
// WHAT IT PUBLISHES IS THE MANUAL AS READ, AND WHAT PHASE 5 SIGNED OFF: the splash, the
// step labels, every step's sub-steps — and, for each sub-step with a validated
// fitting, the clip of it. Each sub-step is a screen on the public page; a screen with a clip
// plays it, and a screen without one keeps the empty frame.
//
// A CLIP IS NOT A FILE. It is the movement and the addresses of the meshes it moves (see
// assemblyClip), so publishing one is publishing a dozen numbers — the visitor's browser
// computes the frames, from the same poseAt the fitting was signed off through. There is
// nothing to render, nothing to upload, and no way for what plays to drift from what was
// approved.
// A step with a single action comes back from the analysis with an empty letter, and the
// public page keys such a screen "a" — so the two line up whatever the analysis returned.
const substepKeyOf = (letter: string | null | undefined) => (letter || 'a').toLowerCase();

export function PublishStep({
  docId,
  file,
  name,
  active,
}: {
  docId: string;
  // The manual's bytes: the published parts are cropped out of them.
  file: Blob;
  name?: string;
  active: boolean;
}) {
  // The category / time come off the manual; the brand, the product name and the
  // splash image are whichever source Phase 1 settled on (the manual's pages, or the
  // web listing) — see infoPicks.
  const brand = useBrand(docId);
  const resolved = useResolvedInfo(docId);
  const { steps } = useAssemblySteps(docId);
  const st = useStepsRun(docId);
  // The supplied parts and the assembled pieces, to write each sub-step's sentence out of its
  // own parts list before it is published. Both languages, since both are published.
  const { items } = usePartsRun(docId);
  const pieceEn = pieceNames(steps, st.subSteps, 'en');
  const pieceFr = pieceNames(steps, st.subSteps, 'fr');
  const heroImage = useListingSplash(docId);
  const parts = usePublishParts(docId, file);
  // EVERY SIGNED-OFF FITTING, keyed the way this page keys its screens — "1A", "2B".
  const clips = useClips(docId);
  const slots = useAssemblySlots(docId);
  const said = useMemo(() => new Map(slots.map((slot) => [slot.key, slot])), [slots]);

  const snapshot: PublishSnapshot = useMemo(() => {
    const cleanTitle = (name ?? '').replace(/\.pdf$/i, '').trim();
    // WHICH RUN EACH SUB-STEP IS IN, as the number the visitor's page will group and count by
    // — resolved HERE rather than passed through as the analysis's own key.
    //
    return {
      docId,
      title: cleanTitle,
      brand: resolved.brand.value,
      // Each step's label is the first line of its transcribed English instruction
      // (falling back to the first available), plus its sub-steps parsed from the
      // generated description (the lettered a./b./c. lines). The full step -> sub-step
      // structure IS what is published: the public page shows a screen for every one of
      // them. A single-action step (no lettered lines) is one sub-step keyed "a".
      // A STEP WITH NO GESTURES IS NOT A SCREEN. Phase 4 answers an empty list for a step
      // whose whole content joins nothing — standing the finished chair on its legs — and
      // this used to publish one blank sub-step for it: a screen with no sentence, no
      // animation and nothing to do, between two screens that have all three. The step's
      // instruction is not lost by leaving it out, because a screen showing nothing was never
      // where a reader was going to find it.
      //
      // It also covers the step nobody has analysed, which published the same empty screen
      // for a different reason. Publication already waits on Phase 4 having run over every
      // step (see phaseProgress), so what this drops is a step that came back with nothing.
      // AND ONLY WHAT WAS SIGNED OFF GOES OUT. A sub-step with no clip is one nobody
      // validated, and on the public page it was a screen reading "Video coming soon"
      // between two that play — a promise, on a page printed onto somebody's packaging,
      // that nothing here will ever keep. What is published is the finished part of the
      // manual; the rest is published the day it is signed off, by re-publishing.
      //
      // `clips` is already the validated set (see useClips), so this is the same test
      // asked the other way round: does this sub-step have one.
      steps: steps.filter((s) => (st.subSteps[s.category] ?? []).length).map((s) => {
        const line =
          st.instructions[s.category]?.find((e) => e.lang === 'en')?.text ??
          st.instructions[s.category]?.[0]?.text ??
          '';
        // The step's sub-steps come structured from Phase 4 (letter + bilingual text).
        const substeps = (st.subSteps[s.category] ?? []).map(
          (sub) => {
            const key = substepKeyOf(sub.letter);
            return {
              key,
              // WRITTEN OUT BEFORE IT IS PUBLISHED. Phase 4 stores the sentence as a
              // template over the gesture's own parts (see subStepText); the public page is
              // handed finished words, not this app's notation, and has no parts list to
              // render them from anyway.
              //
              // Straight off `sub.parts` now: every entry here is a gesture Phase 4 answered,
              // where it used to be either that or a stand-in with no parts field at all —
              // which is what the `'parts' in sub` test guarded, and there is no stand-in.
              //
              // THE SLOT'S OWN SENTENCE where there is one: the assembly phase's, which also says
              // what a following sub-step with no movement asks (see saidWith) — that one has no
              // screen here, and its instruction would otherwise be published nowhere.
              textEn: (said.get(`${s.category}#${sub.letter}`)?.textEn
                ?? renderSubStepText(sub.textEn, sub.parts, items, pieceEn, 'en')).slice(0, MAX_INSTRUCTION),
              textFr: (said.get(`${s.category}#${sub.letter}`)?.textFr
                ?? renderSubStepText(sub.textFr, sub.parts, items, pieceFr, 'fr')).slice(0, MAX_INSTRUCTION),
              // AND THE SAME IN BEATS, as Phase 6 shows them under the clip (see captionAt).
              beatsEn: (said.get(`${s.category}#${sub.letter}`)?.beatsEn ?? []).map((b) => b.slice(0, MAX_INSTRUCTION)),
              beatsFr: (said.get(`${s.category}#${sub.letter}`)?.beatsFr ?? []).map((b) => b.slice(0, MAX_INSTRUCTION)),
            };
          },
        );
        // Cap to match the backend's MAX_TEXT_CHARS snapshot-string limit.
        return { step: s.num, label: line.split('\n')[0].slice(0, 300), substeps };
      })
        // The clip key is the screen's name — the step number and the sub-step's letter,
        // upper-cased — which is what the page looks one up by (see _clip_for).
        .map((s) => ({
          ...s,
          substeps: s.substeps.filter((sub) => clips[subStepLabel(s.step, sub.key)]),
        }))
        // A step whose sub-steps have all gone is not a screen either — the same rule as
        // the step Phase 4 answered nothing for, one line above.
        .filter((s) => s.substeps.length),
      info: {
        // Headline inputs. The splash composes them (see `tutorialHeading`); the
        // file name is never a headline — it is a storage name, not a product name.
        name: resolved.productName.value.trim(),
        category: (brand.category || '').trim(),
        time: (brand.time || '').trim(),
        heroImage: heroImage ?? '',
      },
      // THE CLIPS, whole, keyed by screen. Sent every publish rather than diffed: a snapshot
      // is what the page IS, and a fitting corrected after the first publish has to be able
      // to replace the one on the page — which is what "re-publish" means everywhere else in
      // here.
      clips,
      parts,
    };
  }, [
    docId, name, resolved.brand.value, resolved.productName.value, brand.category, brand.time,
    heroImage, steps, st.instructions, st.subSteps, clips, said, parts,
  ]);

  // What has to exist before there is a page worth printing a QR for: one reconstructed
  // mesh, the last thing this pipeline actually makes. Gating on nothing at all would let
  // an unread manual be published as a tutorial.
  const meshes = usePartModelsStage(docId);
  // AND A SCREEN TO SHOW: a validated sub-step, once the clips can be told (see PublishBlock).
  const { loaded: clipsLoaded } = useDrawableParts(docId);
  const blocked: PublishBlock = meshes.fraction <= 0 ? 'needModel'
    : !clipsLoaded ? 'loading'
    : snapshot.steps.length === 0 ? 'needClip'
    : null;

  return (
    <div className="flex flex-col gap-6">
      {/* The publish panel IS this phase's controls, so it gets the same divider every other
          phase draws between its controls and its results. Nothing under it: the review of
          every sub-step that used to sit there repeated the phases before it, and what the page
          asks of devices is the Admin page's (App analytics) (Louis 2026-10-26). */}
      {/* keepInDemo: this block is the phase's controls AND its answer — the published
          page's link and QR live in it. PublishPanel drops the Update/Unpublish pair on its
          own in the demo and leaves the link, which is the half worth showing. */}
      <PhaseControls keepInDemo>
        <PublishPanel snapshot={snapshot} active={active} blocked={blocked} />
      </PhaseControls>
    </div>
  );
}
